import { useMemo } from "react";
import { Modal, Progress, SimpleTable, Badge, Button } from "@components";
import { formatAmount } from "@utils";
import { statusLabel, evaluateStatus } from "../utils";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";

const statusColors = {
  ready: "blue",
  amountTooSmall: "red",
  noContactDetails: "red",
  noPhone: "red",
  noEmail: "red",
  done: "green",
  sending: "blue",
  sendingFailed: "red",
  notSent: "zinc",
};

export const BatchSummaryModal = ({
  title,
  isOpen,
  onClose,
  currentAmount,
  maxAmount,
  phase,
  rows,
  totals,
  invoicesRequested,
  smsSelected,
  emailSelected,
  onSend,
  // optional selection controls
  isRowSelected,
  onToggleRow,
  onToggleAll,
  isErrorStatus,
}) => {
  const isDisabled = (row) => {
    const st = evaluateStatus(row, { sms: smsSelected, email: emailSelected });
    return isErrorStatus(st);
  };
  const sortedRows = useMemo(() => {
    if (!rows || rows.length === 0) return rows || [];
    const baseRows =
      phase !== "review" ? rows.filter((r) => !isDisabled(r)) : rows;
    return [...baseRows].sort((a, b) => {
      const aDisabled = isDisabled(a);
      const bDisabled = isDisabled(b);
      if (aDisabled === bDisabled) return 0;
      return aDisabled ? 1 : -1;
    });
  }, [rows, smsSelected, emailSelected, phase]);

  const tableConfig = useMemo(
    () => ({
      columns: [
        {
          title: "Customer",
          width: "200px",
          render: (r) => (
            <span className={isDisabled(r) ? "opacity-40" : ""}>
              {r.customerName}
            </span>
          ),
        },
        {
          title: invoicesRequested,
          width: "120px",
          render: (r) => (
            <span className={isDisabled(r) ? "opacity-40" : ""}>
              {formatAmount(r.amount || r.minimumAmount)}
            </span>
          ),
          shouldShow: () => invoicesRequested !== "None",
        },
        {
          title: "Reference",
          width: "150px",
          render: (r) =>
            r.referenceError ? (
              <span className="text-red-500">{r.referenceError}</span>
            ) : (
              <span className={isDisabled(r) ? "opacity-40" : ""}>
                {r.reference}
              </span>
            ),
        },
        {
          title: "Phone",
          width: "150px",
          render: (r) =>
            r.phoneError ? (
              <span className="text-red-500">{r.phone || "No phone"}</span>
            ) : (
              <span className={isDisabled(r) ? "opacity-40" : ""}>
                {r.phone}
              </span>
            ),
          shouldShow: () => smsSelected,
        },
        {
          title: "Email",
          render: (r) =>
            r.emailError ? (
              <span className="text-red-500">{r.email || "No email"}</span>
            ) : (
              <span className={isDisabled(r) ? "opacity-40" : ""}>
                {r.email}
              </span>
            ),
          shouldShow: () => emailSelected,
        },
        {
          title: "Status",
          width: "140px",
          sticky: true,
          render: (r) => (
            <Badge color={statusColors[r.status]}>
              {statusLabel(r.status)}
            </Badge>
          ),
        },
      ],
    }),
    [smsSelected, emailSelected, invoicesRequested],
  );

  const selectedCount = useMemo(() => {
    if (!rows || !rows.length || !isRowSelected) return 0;
    return rows.reduce((count, r) => (isRowSelected(r) ? count + 1 : count), 0);
  }, [rows, isRowSelected]);

  const progressEnd = useMemo(() => {
    if (!rows || !rows.length || !isRowSelected) return 0;
    if (invoicesRequested === "None") return selectedCount;
    // Sum of minimumAmount for selected rows
    return rows
      .filter((r) => isRowSelected(r))
      .reduce((sum, r) => sum + (r.minimumAmount || 0), 0);
  }, [rows, isRowSelected, invoicesRequested, selectedCount]);

  // Disabled rows summary (only show during review)
  const disabledSummary = useMemo(() => {
    if (phase !== "review" || !rows || rows.length === 0) return null;
    const disabled = rows.filter((r) => isDisabled(r));
    const count = disabled.length;
    if (count === 0) return null;

    const amount =
      invoicesRequested === "None"
        ? 0
        : disabled.reduce((sum, r) => sum + (r.minimumAmount || 0), 0);

    const reasonsSet = new Set(
      disabled
        .map((r) => {
          const st = evaluateStatus(r, {
            sms: smsSelected,
            email: emailSelected,
          });
          if (st === "amountTooSmall")
            return "the request amount being too low";
          if (st === "noPhone") return "an invalid mobile number";
          if (st === "noEmail") return "an invalid email address";
          if (st === "noContactDetails") return "invalid contact details";
          return null;
        })
        .filter(Boolean),
    );
    const reasons = Array.from(reasonsSet);
    const reasonsText =
      reasons.length > 1
        ? reasons.slice(0, -1).join(", ") + " or " + reasons[reasons.length - 1]
        : reasons[0];

    const amountText =
      invoicesRequested === "None" ? "" : ` (${formatAmount(amount)})`;

    return `${count} account${
      count > 1 && "s"
    }${amountText} cannot be sent a request due to ${reasonsText}`;
  }, [phase, rows, invoicesRequested, smsSelected, emailSelected, isDisabled]);

  return (
    <Modal size="xl" title={title} isOpen={isOpen} onClose={onClose}>
      {/* Amount header */}

      {/* Progress */}
      {disabledSummary && (
        <div className="p-5 border-b border-zinc-200 text-red-600 text-sm font-semibold inline-flex items-center gap-2 w-full">
          <ExclamationTriangleIcon className="size-4" />
          {disabledSummary}
        </div>
      )}
      <Progress
        current={currentAmount}
        end={progressEnd}
        status={
          phase === "processing"
            ? "Processing"
            : phase === "complete"
              ? "Complete"
              : "Ready"
        }
        format={(val) =>
          invoicesRequested === "None" ? val : formatAmount(val)
        }
      >
        {phase === "complete" && (
          <div className="flex w-full">
            {smsSelected && <p className="text-sm">{totals.sms} SMS sent</p>}
            {emailSelected && (
              <p className="text-sm ml-auto">{totals.email} Emails sent</p>
            )}
          </div>
        )}
      </Progress>

      {/* Table */}
      <SimpleTable
        data={sortedRows}
        config={tableConfig}
        noFooter
        isRowSelected={isRowSelected}
        onToggleRow={onToggleRow}
        onToggleAll={onToggleAll}
        isRowDisabled={isDisabled}
        selectionDisabled={phase !== "review"}
        lazyLoad
      />

      {/* Footer buttons */}
      <div className="p-5 flex justify-end items-center gap-2 sticky bottom-0 z-10 bg-white border-t border-zinc-200">
        {phase !== "complete" ? (
          <Button
            color="primary"
            onClick={onSend}
            disabled={phase === "processing" || selectedCount === 0}
          >
            {phase === "processing" ? "Sending…" : "Send requests"}
          </Button>
        ) : (
          <Button color="primary" onClick={onClose}>
            Done
          </Button>
        )}
      </div>
    </Modal>
  );
};
